import {
    Button,
    Popover,
    ScrollArea,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import {useMemo, useState} from 'react';
import {TablerIconImage} from '../common/TablerIconImage';
import tablerIconNames from '../../data/tablerIconNames.json';

const MAX_VISIBLE_ICONS = 120;

type TablerIconPickerProps = {
    value: string;
    onChange: (icon: string) => void;
};

export function TablerIconPicker({value, onChange}: TablerIconPickerProps) {
    const [opened, setOpened] = useState(false);
    const [search, setSearch] = useState('');

    const filteredIcons = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return tablerIconNames.icons.slice(0, MAX_VISIBLE_ICONS);
        }
        return tablerIconNames.icons
            .filter((icon) => icon.includes(query))
            .slice(0, MAX_VISIBLE_ICONS);
    }, [search]);

    return (
        <Stack gap="xs">
            <Text size="sm" fw={500}>Icon</Text>
            <Popover opened={opened} onChange={setOpened} position="bottom-start" width={360}>
                <Popover.Target>
                    <Button
                        variant="default"
                        leftSection={<TablerIconImage icon={value} size={20}/>}
                        onClick={() => setOpened((current) => !current)}
                    >
                        {value}
                    </Button>
                </Popover.Target>
                <Popover.Dropdown>
                    <Stack gap="sm">
                        <TextInput
                            placeholder="Search icons…"
                            value={search}
                            onChange={(event) => setSearch(event.currentTarget.value)}
                            autoFocus
                        />
                        <ScrollArea h={280} type="auto">
                            <SimpleGrid cols={6} spacing="xs">
                                {filteredIcons.map((icon) => (
                                    <UnstyledButton
                                        key={icon}
                                        onClick={() => {
                                            onChange(icon);
                                            setOpened(false);
                                        }}
                                        aria-label={`Select icon ${icon}`}
                                        style={{
                                            border:
                                                icon === value
                                                    ? '2px solid var(--mantine-color-blue-6)'
                                                    : '1px solid var(--mantine-color-default-border)',
                                            borderRadius: 8,
                                            padding: 8,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--mantine-color-text)',
                                        }}
                                    >
                                        <TablerIconImage icon={icon} size={22}/>
                                    </UnstyledButton>
                                ))}
                            </SimpleGrid>
                        </ScrollArea>
                        {filteredIcons.length === MAX_VISIBLE_ICONS && (
                            <Text size="xs" c="dimmed">
                                Showing first {MAX_VISIBLE_ICONS} matches. Refine your search.
                            </Text>
                        )}
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        </Stack>
    );
}
